/**
 * Which files may be uploaded to the companion server: receipt photos and
 * scanned documents, at most `MAX_UPLOAD_BYTES` each.
 *
 * Shared so the server and the apps agree. The server enforces it on every
 * upload; a client checks first, so a file the server would refuse is never
 * sent.
 *
 * A file's type is decided by its first bytes, never by a declared media type
 * or a file name. The type the bytes are is what the file is stored and later
 * served as, so trusting a label would let an upload marked `image/png` come
 * back as whatever it really is - HTML, say - under the server's origin.
 */

export const UPLOAD_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'image/tiff'] as const;
export type UploadFileType = (typeof UPLOAD_FILE_TYPES)[number];

/** The largest file the server accepts: 8 MB. */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/** A media type that names no type in particular: the bytes decide. */
export const UNTYPED_UPLOAD = 'application/octet-stream';

const FILE_EXTENSIONS: Record<UploadFileType, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/pdf': 'pdf',
  'image/tiff': 'tif',
};

const SIGNATURES: ReadonlyArray<{ type: UploadFileType; bytes: readonly number[] }> = [
  { type: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { type: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  // `%PDF-`
  { type: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46, 0x2d] },
  // Little- and big-endian TIFF, then little- and big-endian BigTIFF.
  { type: 'image/tiff', bytes: [0x49, 0x49, 0x2a, 0x00] },
  { type: 'image/tiff', bytes: [0x4d, 0x4d, 0x00, 0x2a] },
  { type: 'image/tiff', bytes: [0x49, 0x49, 0x2b, 0x00] },
  { type: 'image/tiff', bytes: [0x4d, 0x4d, 0x00, 0x2b] },
];

/** How many leading bytes `detectFileType` looks at; a caller may read only these. */
export const FILE_SIGNATURE_BYTES = Math.max(...SIGNATURES.map((signature) => signature.bytes.length));

/** The upload type the bytes are, by their magic number, or null for anything else. */
export function detectFileType(data: Uint8Array): UploadFileType | null {
  for (const { type, bytes } of SIGNATURES) {
    if (data.length >= bytes.length && bytes.every((byte, index) => data[index] === byte)) return type;
  }
  return null;
}

/** Why a file may not be uploaded. */
export type UploadFileProblem =
  /** No bytes at all. */
  | 'empty'
  /** Over the size limit. */
  | 'too_large'
  /** Not JPEG, PNG, PDF or TIFF. */
  | 'unsupported_type'
  /** An approved type, but not the one it was declared as. */
  | 'type_mismatch';

export type UploadFileVerdict =
  | { ok: true; type: UploadFileType; byteSize: number }
  | { ok: false; problem: UploadFileProblem; detectedType: UploadFileType | null; byteSize: number };

export interface UploadFileOptions {
  /**
   * The media type the file is labelled with, if any; parameters are ignored.
   * Empty or `application/octet-stream` defers to the bytes; anything else
   * must be what the bytes are.
   */
  declaredType?: string | null;
  /**
   * The file's full size, when `data` holds only its first bytes (at least
   * `FILE_SIGNATURE_BYTES`). Defaults to `data.length`.
   */
  byteSize?: number;
  /** A lower limit than `MAX_UPLOAD_BYTES`; a higher one is ignored. */
  maxBytes?: number;
}

/**
 * Whether a file may be uploaded, and if so the type it is stored as.
 *
 * `data` is the file, or at least its first `FILE_SIGNATURE_BYTES` with
 * `options.byteSize` giving the whole size.
 */
export function validateUploadFile(data: Uint8Array, options: UploadFileOptions = {}): UploadFileVerdict {
  const byteSize = options.byteSize ?? data.length;
  const limit = Math.min(options.maxBytes ?? MAX_UPLOAD_BYTES, MAX_UPLOAD_BYTES);
  const detectedType = detectFileType(data);
  const refuse = (problem: UploadFileProblem): UploadFileVerdict => ({ ok: false, problem, detectedType, byteSize });

  if (byteSize === 0) return refuse('empty');
  if (byteSize > limit) return refuse('too_large');
  if (!detectedType) return refuse('unsupported_type');

  const declared = (options.declaredType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  if (declared !== '' && declared !== UNTYPED_UPLOAD && declared !== detectedType) return refuse('type_mismatch');

  return { ok: true, type: detectedType, byteSize };
}

/**
 * The media type to serve stored bytes under: what they are, or opaque bytes
 * for anything unrecognised - never a type recorded alongside them.
 */
export function servedFileType(data: Uint8Array): string {
  return detectFileType(data) ?? UNTYPED_UPLOAD;
}

/** The file extension for bytes of an upload type, `bin` for anything else. */
export function fileExtension(data: Uint8Array): string {
  const type = detectFileType(data);
  return type ? FILE_EXTENSIONS[type] : 'bin';
}

/** Whether a vision model can read the file as a picture (a PDF or TIFF it cannot). */
export function isRasterPhoto(type: UploadFileType | null): boolean {
  return type === 'image/jpeg' || type === 'image/png';
}
